import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import { UserRoles } from 'types/users';
import uniqBy from 'lodash.uniqby';
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import { OrderStatus } from 'types/orders';
import { SubscriptionStatus } from 'components/Admin/organisms/Subscriptions/types';

const UsersSelectors = (state: RootState) => state.users;

export const selectProfile = createSelector(
  UsersSelectors,
  (state) => state.profile,
);

export const selectAllUsers = createSelector(UsersSelectors, (state) =>
  uniqBy(state.items, 'id'),
);

export const selectAllUsersWithOrders = createSelector(
  selectAllUsers,
  (allUsers) => {
    return allUsers.filter((item) => item.orders && item.orders.length > 0);
  },
);
export const selectUsersPendingStatus = createSelector(
  UsersSelectors,
  (state) => state.isPending,
);

export const selectUserNotes = createSelector(
  UsersSelectors,
  (state) => state.notes,
);

export const selectUserNotesPendingStatus = createSelector(
  UsersSelectors,
  (state) => state.isUserNotesPending,
);

export const selectAllStaffAccounts = createSelector(
  selectAllUsers,
  (allUsers) => allUsers.filter((item) => item.role === UserRoles.ADMIN),
);
export const selectAllOwnerAccounts = createSelector(
  selectAllUsers,
  (allUsers) => {
    return allUsers.filter((item) => item.role === UserRoles.SUPER_ADMIN);
  },
);
export const selectSelectedUser = createSelector(
  UsersSelectors,
  (state) => state.selectedUser,
);

export const selectPasswordPendingStatus = createSelector(
  UsersSelectors,
  (state) => state.passwordIsPending,
);

export const selectNewDraftUser = createSelector(
  UsersSelectors,
  (state) => state.newUser,
);

export const selectSecondaryDriver = createSelector(
  UsersSelectors,
  (state) => state.secondaryDriver,
);
export const selectToken = createSelector(
  UsersSelectors,
  (state) => state.token,
);
export const selectUserAuthStatus = createSelector(
  [selectProfile, selectToken],
  (profile, token) => {
    if (!profile || !token) {
      return false;
    }

    return Boolean(profile.id);
  },
);

export const selectProfileActiveSubscription = createSelector(
  selectProfile,
  (profile) => {
    if (!profile) {
      return null;
    }
    const subscriptions = profile.subscriptions;
    if (!subscriptions || isEmpty(subscriptions)) {
      return null;
    }
    return subscriptions
      .filter(
        (s) =>
          s.status !== SubscriptionStatus.ARCHIVED &&
          s.status !== SubscriptionStatus.CANCELED,
      )
      .find((item) => moment.utc(item.endDate).isAfter(moment.utc()));
  },
);

export const selectIsSecondaryDriverCreating = createSelector(
  UsersSelectors,
  (state) => state.isSecondaryDriverCreating,
);

export const selectIsSocialLoginPending = createSelector(
  UsersSelectors,
  (state) => state.isSocialLoginPending,
);

export const selectUserInProgressOrder = createSelector(
  selectProfile,
  (profile) => {
    if (!profile || !profile.orders) {
      return;
    }
    return profile.orders
      .filter(
        (item) =>
          ![
            OrderStatus.REFUSED,
            OrderStatus.DONE,
            OrderStatus.ARCHIVED,
            OrderStatus.OUTDATED,
          ].includes(item.status),
      )
      .find((item) => moment.utc(item.rentEndDate).isSameOrAfter(moment.utc()));
  },
);
